import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { ChevronLeft, KeyRound, Lock, Mail } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { SafecheckLogo } from "../components/SafecheckLogo";
import { resetPasswordWithCode } from "../api/authApi";
import { cn } from "@/shared/lib/cn";

const inputClass =
  "h-12 w-full rounded-xl border border-border bg-card-muted pl-11 pr-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted/70 focus:border-accent focus:ring-2 focus:ring-accent/20";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const infoMessage = searchParams.get("info");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const result = await resetPasswordWithCode({
        email: email.trim(),
        code: code.trim(),
        password,
        password_confirmation: passwordConfirmation,
      });
      navigate("/login", { state: { message: result.message } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Réinitialisation impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-5 py-10">
      <div className="mx-auto w-full max-w-[400px]">
        <div className="mb-8 flex justify-center">
          <SafecheckLogo size="lg" linkToDashboard={false} />
        </div>
        <h1 className="text-2xl font-bold text-foreground">Nouveau mot de passe</h1>
        <p className="mt-2 text-sm text-muted">Entrez le code reçu par email et votre nouveau mot de passe.</p>

        {infoMessage ? (
          <p className="mt-3 rounded-lg border border-primary/25 bg-primary-soft px-3 py-2 text-sm text-primary">
            {infoMessage}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} className="theme-card theme-card-static mt-6 space-y-4 p-6">
          <div>
            <label htmlFor="reset-email" className="mb-2 block text-xs font-semibold uppercase text-muted">
              {t.safecheck.login.email}
            </label>
            <div className="relative">
              <Mail size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-primary" />
              <input
                id="reset-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="reset-code" className="mb-2 block text-xs font-semibold uppercase text-muted">
              Code (6 chiffres)
            </label>
            <div className="relative">
              <KeyRound size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-primary" />
              <input
                id="reset-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                className={inputClass}
                placeholder="000000"
              />
            </div>
          </div>

          <div>
            <label htmlFor="reset-password" className="mb-2 block text-xs font-semibold uppercase text-muted">
              {t.safecheck.login.password}
            </label>
            <div className="relative">
              <Lock size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
              <input
                id="reset-password"
                type="password"
                minLength={6}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="reset-password-confirm" className="mb-2 block text-xs font-semibold uppercase text-muted">
              Confirmer le mot de passe
            </label>
            <div className="relative">
              <Lock size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
              <input
                id="reset-password-confirm"
                type="password"
                minLength={6}
                required
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <button
            type="submit"
            disabled={submitting}
            className={cn(
              "h-11 w-full rounded-xl bg-accent text-sm font-semibold text-accent-foreground",
              submitting && "opacity-60",
            )}
          >
            {submitting ? "Enregistrement…" : "Réinitialiser"}
          </button>
        </form>

        <Link
          to="/login/forgot"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-foreground"
        >
          <ChevronLeft size={16} />
          Renvoyer un code
        </Link>
      </div>
    </div>
  );
}
